import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ExtraNumbers, staffPhoneEndpoints } from "@/components/ExtraNumbers";
import { toast } from "sonner";
import { Phone, ShieldCheck, CheckCircle2, Mail } from "lucide-react";

// The one place a person confirms their own mobile: number → texted code → verified.
// `prefs` is /me/notification-prefs ({phone, phone_verified, email}); `onChanged` re-fetches it.
export const PhoneVerify = ({ prefs, onChanged, compact = false }) => {
  const [phoneInput, setPhoneInput] = useState("");
  const [changing, setChanging] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCooldown = (s) => {
    setCooldown(s);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; }), 1000);
  };
  const sendCode = async () => {
    setBusy("send");
    try {
      const r = await api.post("/me/phone/send-code", { phone: phoneInput });
      setCodeSent(true); startCooldown(r.data.cooldown || 45);
      toast[r.data.sent ? "success" : "message"](r.data.sent ? "Code texted to your phone" : "We tried to text that number — enter the code if it arrives.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send a code."); }
    setBusy("");
  };
  const verifyCode = async () => {
    setBusy("verify");
    try {
      await api.post("/me/phone/verify", { code });
      setCodeSent(false); setChanging(false); setCode(""); setPhoneInput("");
      toast.success("Mobile verified — text and call alerts can reach you now.");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed."); }
    setBusy("");
  };

  const verified = prefs?.phone && prefs?.phone_verified;
  return (
    <div data-testid="phone-verify" className="space-y-3">
      {!compact && (
        <div className="rounded-2xl border border-sand-200 bg-sand-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
            <Mail className="h-4 w-4 text-ink/50 shrink-0" strokeWidth={2} />
            <span className="text-ink/60">Email</span>
            <span className="font-semibold text-ink truncate" data-testid="notif-header-email">{prefs?.email || "no email on file"}</span>
          </div>
          <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
            <Phone className="h-4 w-4 text-ink/50 shrink-0" strokeWidth={2} />
            <span className="text-ink/60">Text</span>
            {verified && !changing ? (
              <>
                <span className="font-semibold text-ink truncate" data-testid="notif-header-phone">{prefs.phone}</span>
                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5"><CheckCircle2 className="h-2.5 w-2.5" strokeWidth={3} /> Verified</span>
                <button data-testid="phone-change-btn" onClick={() => { setChanging(true); setCodeSent(false); }} className="ml-auto text-xs font-semibold text-redline-600 hover:text-redline-700">Change</button>
              </>
            ) : <span className="text-ink/50 italic">Verify a number to unlock text alerts</span>}
          </div>
        </div>
      )}
      {compact && verified && !changing && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-ink" data-testid="notif-header-phone">{prefs.phone}</span>
          <span data-testid="phone-verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold px-2 py-0.5"><CheckCircle2 className="h-3 w-3" strokeWidth={3} /> Verified — text &amp; call alerts reach you</span>
          <button data-testid="phone-change-btn" onClick={() => { setChanging(true); setCodeSent(false); }} className="text-xs font-semibold text-redline-600 hover:text-redline-700">Change number</button>
        </div>
      )}
      {(!verified || changing) && (
        <div className={compact ? "" : "rounded-xl border border-sand-200 bg-white p-3.5"}>
          {compact && !verified && (
            <p data-testid="phone-unverified-note" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
              {prefs?.phone ? <>We have <span className="font-semibold">{prefs.phone}</span> on file but it isn't verified — so we can email you, but not text or call you. Confirm it below.</>
                : "No mobile on file — add one so text and call alerts (including emergencies) can reach you."}
            </p>
          )}
          <label className="text-sm font-semibold flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-redline-500" /> {verified ? "New mobile number" : "Your mobile number"}</label>
          <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
            <input data-testid="phone-input" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} disabled={busy === "send"}
              placeholder={prefs?.phone && !verified ? prefs.phone : "+15125550123"} inputMode="tel"
              className="min-w-0 flex-1 rounded-xl border border-sand-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <button data-testid="phone-send-code" onClick={sendCode} disabled={!phoneInput || busy === "send" || cooldown > 0}
              className="rounded-xl bg-ink text-white text-sm font-semibold px-4 py-2.5 hover:bg-ink-700 transition-colors disabled:opacity-50 whitespace-nowrap sm:w-28 shrink-0 text-center tabular-nums">
              {busy === "send" ? "Sending…" : cooldown > 0 ? `Resend ${cooldown}s` : codeSent ? "Resend" : "Send code"}
            </button>
          </div>
          {prefs?.phone && !verified && !phoneInput && (
            <button type="button" data-testid="phone-use-on-file" onClick={() => setPhoneInput(prefs.phone)} className="mt-1.5 text-xs font-semibold text-redline-600 hover:text-redline-700">Use {prefs.phone}</button>
          )}
          {codeSent && (
            <div className="mt-3">
              <label className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-redline-500" /> Enter the 6-digit code we texted</label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                <input data-testid="phone-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric"
                  placeholder="123456" className="min-w-0 flex-1 rounded-xl border border-sand-200 px-4 py-2.5 text-sm tracking-[0.3em] font-semibold outline-none focus:ring-2 focus:ring-redline-500/30" />
                <button data-testid="phone-verify-btn" onClick={verifyCode} disabled={code.length !== 6 || busy === "verify"}
                  className="rounded-xl bg-redline-500 text-white text-sm font-semibold px-5 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-50 whitespace-nowrap sm:w-auto shrink-0">
                  {busy === "verify" ? "Verifying…" : "Verify"}
                </button>
              </div>
            </div>
          )}
          {changing && (
            <button onClick={() => { setChanging(false); setCodeSent(false); }} className="text-xs text-muted-foreground hover:text-ink mt-2">Cancel</button>
          )}
        </div>
      )}
      {/* iter378 — additional numbers, each verified on its own. Texts fan out to every verified
          number with alerts on; the emergency ladder rings the primary first. */}
      <div className="mt-4 pt-3 border-t border-sand-200" data-testid="phone-extras-section">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Additional numbers</div>
        <ExtraNumbers endpoints={staffPhoneEndpoints(api)} testid="my-phones" showPrimary={false} onChanged={onChanged} />
      </div>
    </div>
  );
};

// Account settings card: "can we reach you?" — email (proven by signing in) and the mobile flow above.
export const ContactVerificationCard = ({ user }) => {
  const [prefs, setPrefs] = useState(null);
  const load = () => api.get("/me/notification-prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({ phone: "", phone_verified: false, email: user?.email || "" }));
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const verified = prefs?.phone && prefs?.phone_verified;
  return (
    <div id="account-verify" data-testid="account-verify" className={`rounded-2xl border bg-white p-4 sm:p-6 scroll-mt-20 ${prefs && !verified ? "border-amber-300" : "border-sand-200"}`}>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={1.75} />
        <h2 className="font-heading text-lg font-bold text-ink">Contact &amp; verification</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">How Ask Redline reaches you — and, if you're on the emergency ladder, how it calls you.</p>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-ink/50 shrink-0" strokeWidth={2} />
          <span className="text-ink/60">Email</span>
          <span className="font-semibold text-ink truncate" data-testid="verify-email">{prefs?.email || user?.email || "no email on file"}</span>
          {(prefs?.email || user?.email) && <span data-testid="email-verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold px-2 py-0.5"><CheckCircle2 className="h-3 w-3" strokeWidth={3} /> Verified — you sign in with it</span>}
        </div>
        <div className="flex items-start gap-2">
          <Phone className="h-4 w-4 text-ink/50 shrink-0 mt-2.5" strokeWidth={2} />
          <div className="min-w-0 flex-1">
            <span className="text-sm text-ink/60 block mb-1.5">Mobile</span>
            {prefs ? <PhoneVerify prefs={prefs} onChanged={load} compact /> : <div className="h-5 w-5 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" />}
          </div>
        </div>
      </div>
    </div>
  );
};
